import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRoute } from 'wouter';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdAlert, CmdError } from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import { useQueries } from '@tanstack/react-query';
import localforage from 'localforage';
import { IWidgetGraph } from '../../OnlinePanel/OnlinePanelState';
import { IVehicle } from '../../Vehicles/types';
import { URL_FUEL_LEVEL_LITERS, URL_GRAPHS } from '../../../router/constants';
import {
  getDataByDates,
  getVehicleName,
  getIsCompressed,
  getIsEndDateToday,
  IGraphResponseData,
  getDateFrom,
  getDateTo,
  getPartDataByDates,
  getTanksCapacity,
  todayDateRange,
} from '../GraphService';
import TableDatepicker from '../TableDatepicker';
import OnlineGraph from './OnlineGraph';
import Stopped from './Stopped';
import PartSelecting from '../PartSelecting';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import PositionDetails from '../PositionsDetails';
import {
  fuelLevelLitersWidgetGraphAtom,
  isGraphOpenAtom,
  lastClickedTimestampAtom,
  widgetGraphSelectedValuesAtom,
} from '../GraphState';
import { setLoaderAtom } from '../../../store/recoil/AppState';
import { vehicleByIdSelector } from '../../../store/recoil/vehicles';

interface IFuelLevelPercOnlineProps {
  vehicleId?: number;
}

export default function FuelLevelPercOnline(props: IFuelLevelPercOnlineProps) {
  const [, params] = useRoute(`${URL_GRAPHS}${URL_FUEL_LEVEL_LITERS}/:vehicleId`);
  const { formatMessage: f } = useIntl();

  const setWidgetGraphSelectedValues = useSetRecoilState<string[] | null>(
    widgetGraphSelectedValuesAtom
  );
  const setLastClickedTimestamp = useSetRecoilState<string>(lastClickedTimestampAtom);
  const [vehicleTankCapacity, setVehicleTankCapacity] = React.useState(0);
  const [rangeError, setRangeError] = React.useState('');
  const [isToday, setIsToday] = React.useState(true);
  const [selectedDate, setSelectedDate] = React.useState<Date[]>(todayDateRange);
  const [selectedPartDate, setSelectedPartDate] = React.useState<Date[] | null>(null);
  const [responseData, setResponseData] = React.useState<IGraphResponseData[]>([]);
  const setLoader = useSetRecoilState<boolean>(setLoaderAtom);
  const [fuelLevelLitersWidgetGraph, setFuelLevelLitersWidgetGraph] =
    useRecoilState<IWidgetGraph | null>(fuelLevelLitersWidgetGraphAtom);
  const vehicle = useRecoilValue<IVehicle | null>(
    vehicleByIdSelector(
      props.vehicleId || (params && params.vehicleId ? Number(params.vehicleId) : false)
    )
  );
  const setIsGraphOpen = useSetRecoilState<boolean | number>(isGraphOpenAtom);

  const { setDocumentTitle } = useDocumentTitle();

  React.useEffect(() => {
    if (vehicle) {
      setIsGraphOpen(vehicle.id);
    }
  }, [setIsGraphOpen, vehicle]);

  React.useEffect(() => {
    if (vehicle) {
      const documentTitle = `${f(
        { id: 'widget.fuelLevel.tooltip' },
        { valueType: 'L' }
      )} ${getVehicleName(vehicle)}`;
      setDocumentTitle(documentTitle);
    }
  }, [f, setDocumentTitle, vehicle]);

  const resetWidgetGraphSelectedValues = () => {
    setWidgetGraphSelectedValues(null);
    setSelectedPartDate(null);
    setSelectedDate(todayDateRange);
  };

  const handleData = async () => {
    if (vehicle) {
      setLoader(true);
      resetWidgetGraphSelectedValues();
      const response = await getDataByDates('consAbs', String(vehicle.id));
      setLoader(false);
      if (response) {
        if (fuelLevelLitersWidgetGraph) {
          setFuelLevelLitersWidgetGraph((oldState: any) => {
            return { ...oldState, timestamp: response[0]?.timestamp };
          });
        }
        setResponseData(response);
        if (params) {
          setLastClickedTimestamp(response[response.length - 1]?.timestamp);
        }
      } else if (!params && fuelLevelLitersWidgetGraph) {
        setFuelLevelLitersWidgetGraph((oldState: any) => {
          return { ...oldState, timestamp: '' };
        });
      } else {
        setResponseData([]);
        setLastClickedTimestamp('');
      }
    }
  };

  const handleDataByDates = async (dates?: Date[] | null): Promise<void> => {
    if (vehicle) {
      resetWidgetGraphSelectedValues();
      if (dates && dates.length === 2) {
        setIsToday(getIsEndDateToday(dates[1]));
        setRangeError('');
        setSelectedDate(dates);
        setLoader(true);
        const response = await getDataByDates('consAbs', String(vehicle.id), dates);
        if (response) {
          setResponseData(response);
          setLastClickedTimestamp(response[response.length - 1]?.timestamp);
        } else {
          setResponseData([]);
          setLastClickedTimestamp('');
        }
        setLoader(false);
      } else if (dates === null) {
        setRangeError(f({ id: 'datepicker.spanFromToValidation' }));
      } else {
        setRangeError(f({ id: 'errors.reports.cannotSelectDateLongerThanMonth' }));
      }
    }
  };

  const reloadData = async (): Promise<void> => {
    if (vehicle) {
      resetWidgetGraphSelectedValues();
      if (selectedDate && selectedDate.length === 2) {
        setIsToday(getIsEndDateToday(selectedDate[1]));
        setRangeError('');
        setLoader(true);
        const response = await getDataByDates('consAbs', String(vehicle.id), selectedDate);
        if (response) {
          setResponseData(response);
          if (params) {
            setLastClickedTimestamp(response[response.length - 1]?.timestamp);
          }
        } else {
          setResponseData([]);
          setLastClickedTimestamp('');
        }
        setLoader(false);
      }
    }
  };

  const handleTankCapacity = async (): Promise<void> => {
    if (vehicle) {
      const data = await getTanksCapacity(vehicle.id);
      if (data !== false) {
        setVehicleTankCapacity(data);
        localforage.setItem('primaryTankCapacity', { vehicleId: vehicle.id, value: data });
      }
    }
  };

  const getTankCapacityFromLocalforage = async (): Promise<void> => {
    if (vehicle) {
      const data: { vehicleId: number; value: number } | null =
        await localforage.getItem('primaryTankCapacity');
      if (data && data.vehicleId === vehicle.id) {
        setVehicleTankCapacity(data.value);
      } else {
        handleTankCapacity();
      }
    }
  };

  useQueries({
    queries: [
      {
        queryKey: ['fuelLevelLitersData', vehicle?.id],
        queryFn: () => handleData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!(
          params ||
          (!params && fuelLevelLitersWidgetGraph && !fuelLevelLitersWidgetGraph.timestamp)
        ),
      },
      {
        queryKey: ['primaryTankCapacity', vehicle?.id],
        queryFn: () => getTankCapacityFromLocalforage(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: true,
      },
    ],
  });

  // const getEndOfToday = () => {
  //   if (responseData && responseData[0]) {
  //     const ts = new Date(responseData[0].timestamp);
  //     return new Date(ts.setHours(23, 59, 59, 999));
  //   }
  //   return new Date(new Date().setHours(23, 59, 59, 999));
  // };

  const renderGraph = () => {
    const isCompressed = getIsCompressed(responseData);
    if (vehicle) {
      return isToday ? (
        <OnlineGraph
          vehicleId={vehicle.id}
          responseData={responseData}
          isCompressed={isCompressed}
          isSelectable={isCompressed && !!params}
          selectedDate={selectedDate}
          vehicleTankCapacity={vehicleTankCapacity}
          isDetail={!!params}
          reloadData={reloadData}
        />
      ) : (
        <Stopped
          responseData={responseData}
          isCompressed={isCompressed}
          isSelectable={isCompressed && !!params}
          vehicleTankCapacity={vehicleTankCapacity}
          selectedDate={selectedDate}
        />
      );
    }
    return null;
  };

  const renderCompressedGraphAlert = () => {
    return getIsCompressed(responseData) ? (
      <div style={{ padding: params ? '20px 0 0 0' : '' }}>
        <CmdAlert
          id="velocity-graph-alert"
          data={{
            title: f({ id: 'graph.velocity.alert.title' }),
            message: params
              ? f({ id: 'graph.velocity.alert.message' })
              : f({ id: 'graph.onlinepanel.alert.message' }),
            type: 'info',
          }}
          noCancel
        />
      </div>
    ) : null;
  };

  const loadPartData = async (dateFrom: Date, dateTo: Date) => {
    if (vehicle) {
      setIsToday(false);
      setLoader(true);
      setSelectedPartDate([dateFrom, dateTo]);
      const response = await getPartDataByDates(
        'consAbs',
        String(vehicle.id),
        selectedPartDate || undefined,
        [dateFrom, dateTo]
      );
      if (response) {
        setResponseData(response);
        setLastClickedTimestamp(response[response.length - 1]?.timestamp);
      }
      setLoader(false);
    }
  };

  return (
    <div
      className="container-fluid new-css-wrapper"
      data-cy="fuel-level-liters-graph"
      style={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {params && vehicle ? (
        [
          <div className="row section page-head" data-cy="reports-head">
            <h4 data-cy="reports-title">
              {f({ id: 'widget.fuelLevel.tooltip' }, { valueType: 'L' })}{' '}
              <span className="text-gray-light">{getVehicleName(vehicle)}</span>
            </h4>
          </div>,
          <CmdBox
            id="fuellevelperc-basic-information"
            title={f({ id: 'graph.fuelLevel.section.overview' }, { valueType: 'L' })}
          >
            <div style={{ marginBottom: '10px' }}>
              <div
                data-cy="datapicker-popup-fuellevelperc-graph"
                className="label label-primary mb-xs position-relative label--filter"
                style={{ padding: '9px 8px 8px 8px' }}
              >
                <span>
                  <TableDatepicker
                    name="graph-velocity"
                    type="chip"
                    onChangeCallback={handleDataByDates}
                    defaultDates={selectedPartDate || undefined}
                    // onlyOneDay
                  />
                </span>
              </div>
              {rangeError && (
                <div>
                  <CmdError message={rangeError} />
                </div>
              )}
            </div>
            {getIsCompressed(responseData) && (
              <PartSelecting
                dateFrom={getDateFrom(responseData)}
                dateTo={getDateTo(responseData)}
                loadPartData={loadPartData}
                data={responseData}
                isNotFilterableByRide
              />
            )}
            {renderCompressedGraphAlert()}
            <div style={{ marginTop: '10px', position: 'relative', padding: '20px 0px 20px 25px' }}>
              {renderGraph()}
            </div>
          </CmdBox>,
          <PositionDetails vehicleId={vehicle.id} positions={responseData} />,
        ]
      ) : (
        <div>
          {renderCompressedGraphAlert()}
          {renderGraph()}
        </div>
      )}
    </div>
  );
}
